import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Package, PanelsTopLeft, Rocket, LogOut, ArrowRight, Check } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/components/pepmuse/AuthContext";
import { Reveal, Eyebrow } from "@/components/pepmuse/Reveal";
import { SocialButtons } from "@/components/pepmuse/SocialButtons";

const API = process.env.REACT_APP_BACKEND_URL;

const fieldClass =
  "w-full rounded-xl border border-white/10 bg-ink/70 px-4 py-3 text-sm text-paper placeholder:text-paper/35 transition-[border-color,box-shadow] duration-200 focus:border-lilac/60 focus:outline-none focus:ring-2 focus:ring-lilac/30";
const labelClass = "mb-1.5 block text-sm font-bold text-paper/85";

const AuthCard = () => {
  const { setUser } = useAuth();
  const location = useLocation();
  const [mode, setMode] = useState("signin"); // signin | signup | forgot
  const [values, setValues] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState(location.state?.authError || "");
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const set = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "forgot") {
        const res = await fetch(`${API}/api/auth/forgot-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: values.email }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(formatApiErrorDetail(data.detail));
          return;
        }
        setForgotSent(true);
        return;
      }
      const path = mode === "signin" ? "/api/auth/login" : "/api/auth/register";
      const body =
        mode === "signin" ? { email: values.email, password: values.password } : values;
      const res = await fetch(`${API}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(formatApiErrorDetail(data.detail));
        return;
      }
      setUser(data);
    } catch {
      setError("Couldn't reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (mode === "forgot") {
    return (
      <div className="mx-auto w-full max-w-md rounded-3xl border border-white/10 bg-card p-7 sm:p-9">
        <h1 className="font-display text-2xl font-extrabold text-paper">Reset your password.</h1>
        <p className="mt-2 text-sm text-paper/55">
          Enter your account email — if it exists, a reset link is on its way. It works once, within an hour.
        </p>
        {forgotSent ? (
          <div className="mt-6">
            <p
              role="status"
              data-testid="forgot-success"
              className="rounded-xl border border-lilac/30 bg-lilac/10 p-4 text-sm text-paper/80"
            >
              If an account exists for that email, a reset link is on its way. Check your inbox — and spam, just in
              case.
            </p>
            <button
              type="button"
              data-testid="forgot-back-button"
              onClick={() => {
                setMode("signin");
                setForgotSent(false);
                setError("");
              }}
              className="mt-6 w-full rounded-full border border-white/15 px-7 py-3 text-sm font-bold text-paper transition-colors duration-200 hover:border-lilac/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-card"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <>
            {error && (
              <p role="alert" data-testid="auth-error" className="mt-5 rounded-xl border border-red-400/30 bg-red-400/10 p-3.5 text-sm text-red-200">
                {error}
              </p>
            )}
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label htmlFor="auth-email" className={labelClass}>Email</label>
                <input
                  id="auth-email"
                  data-testid="auth-email-input"
                  type="email"
                  autoComplete="email"
                  required
                  value={values.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={fieldClass}
                />
              </div>
              <button
                type="submit"
                data-testid="forgot-submit-button"
                disabled={busy}
                className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-lilac px-7 py-3.5 text-sm font-bold text-ink transition-[transform,box-shadow,background-color,opacity] duration-200 ease-out hover:-translate-y-0.5 hover:bg-lilac-bright hover:shadow-[0_16px_36px_-12px_rgba(184,167,232,0.55)] active:translate-y-0 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? "Sending…" : "Send reset link"}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
              </button>
              <button
                type="button"
                data-testid="forgot-back-button"
                onClick={() => {
                  setMode("signin");
                  setError("");
                }}
                className="w-full py-1 text-center text-sm font-bold text-paper/50 transition-colors duration-200 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
              >
                Back to sign in
              </button>
            </form>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-md rounded-3xl border border-white/10 bg-card p-7 sm:p-9">
      <div className="grid grid-cols-2 gap-1 rounded-full bg-ink/70 p-1" role="tablist" aria-label="Sign in or create account">
        {[
          { id: "signin", label: "Sign in" },
          { id: "signup", label: "Create account" },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={mode === tab.id}
            data-testid={`${tab.id}-tab`}
            onClick={() => {
              setMode(tab.id);
              setError("");
            }}
            className={`rounded-full py-2.5 text-sm font-bold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac ${
              mode === tab.id ? "bg-lilac text-ink" : "text-paper/55 hover:text-paper"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <h1 className="mt-7 font-display text-2xl font-extrabold text-paper">
        {mode === "signin" ? "Welcome back." : "Start your account."}
      </h1>
      <p className="mt-2 text-sm text-paper/55">
        {mode === "signin"
          ? "Sign in to see your package progress and inquiries."
          : "One account for your brand, your package, and your launch progress."}
      </p>

      {error && (
        <p role="alert" data-testid="auth-error" className="mt-5 rounded-xl border border-red-400/30 bg-red-400/10 p-3.5 text-sm text-red-200">
          {error}
        </p>
      )}

      <div className="mt-6">
        <SocialButtons />
      </div>
      <div className="my-6 flex items-center gap-3" aria-hidden="true">
        <span className="h-px flex-1 bg-white/10" />
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-paper/35">or with email</span>
        <span className="h-px flex-1 bg-white/10" />
      </div>

      <form onSubmit={submit} className="space-y-4">
        {mode === "signup" && (
          <div>
            <label htmlFor="auth-name" className={labelClass}>Name</label>
            <input
              id="auth-name"
              data-testid="auth-name-input"
              type="text"
              autoComplete="name"
              required
              value={values.name}
              onChange={set("name")}
              placeholder="Your name"
              className={fieldClass}
            />
          </div>
        )}
        <div>
          <label htmlFor="auth-email" className={labelClass}>Email</label>
          <input
            id="auth-email"
            data-testid="auth-email-input"
            type="email"
            autoComplete="email"
            required
            value={values.email}
            onChange={set("email")}
            placeholder="you@example.com"
            className={fieldClass}
          />
        </div>
        <div>
          <div className="flex items-baseline justify-between">
            <label htmlFor="auth-password" className={labelClass}>Password</label>
            {mode === "signin" && (
              <button
                type="button"
                data-testid="forgot-password-link"
                onClick={() => {
                  setMode("forgot");
                  setError("");
                }}
                className="rounded-sm text-xs font-bold text-lilac transition-colors duration-200 hover:text-lilac-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
              >
                Forgot password?
              </button>
            )}
          </div>
          <input
            id="auth-password"
            data-testid="auth-password-input"
            type="password"
            autoComplete={mode === "signin" ? "current-password" : "new-password"}
            required
            minLength={8}
            value={values.password}
            onChange={set("password")}
            placeholder={mode === "signin" ? "Your password" : "8+ characters"}
            className={fieldClass}
          />
        </div>
        <button
          type="submit"
          data-testid="auth-submit-button"
          disabled={busy}
          className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-lilac px-7 py-3.5 text-sm font-bold text-ink transition-[transform,box-shadow,background-color,opacity] duration-200 ease-out hover:-translate-y-0.5 hover:bg-lilac-bright hover:shadow-[0_16px_36px_-12px_rgba(184,167,232,0.55)] active:translate-y-0 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? "One moment…" : mode === "signin" ? "Sign in" : "Create account"}
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
        </button>
      </form>
    </div>
  );
};

const STEP_META = [
  { key: "brand", icon: Package, title: "Brand identity" },
  { key: "website", icon: PanelsTopLeft, title: "Website" },
  { key: "launch", icon: Rocket, title: "Launch" },
];

const SettingsCard = ({ user }) => {
  const { setUser } = useAuth();
  const [name, setName] = useState(user.name || "");
  const [nameStatus, setNameStatus] = useState(null);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwStatus, setPwStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const hasPassword = (user.providers || ["password"]).includes("password");

  const saveName = async (e) => {
    e.preventDefault();
    setBusy(true);
    setNameStatus(null);
    try {
      const res = await fetch(`${API}/api/auth/update-profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      setUser(data);
      setNameStatus({ kind: "ok", text: "Name updated." });
    } catch (err) {
      setNameStatus({ kind: "err", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwStatus(null);
    if (pw.next !== pw.confirm) {
      setPwStatus({ kind: "err", text: "New passwords don't match." });
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ current_password: pw.current, new_password: pw.next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      setPw({ current: "", next: "", confirm: "" });
      setPwStatus({ kind: "ok", text: "Password updated. Use it next time you sign in." });
    } catch (err) {
      setPwStatus({ kind: "err", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const statusClass = (s) =>
    `rounded-xl border p-3.5 text-sm ${
      s.kind === "ok" ? "border-[#8FD8B8]/30 bg-[#8FD8B8]/10 text-[#8FD8B8]" : "border-red-400/30 bg-red-400/10 text-red-200"
    }`;

  return (
    <div data-testid="settings-card" className="mt-6 rounded-3xl border border-white/10 bg-card p-7 sm:p-9">
      <h2 className="font-display text-xl font-extrabold text-paper">Account settings</h2>

      <form onSubmit={saveName} className="mt-6">
        <label htmlFor="settings-name" className={labelClass}>Display name</label>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            id="settings-name"
            data-testid="settings-name-input"
            type="text"
            required
            maxLength={120}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameStatus(null);
            }}
            className={fieldClass}
          />
          <button
            type="submit"
            data-testid="settings-name-save"
            disabled={busy || !name.trim() || name.trim() === (user.name || "")}
            className="shrink-0 rounded-full bg-lilac px-6 py-3 text-sm font-bold text-ink transition-[transform,background-color,opacity] duration-200 hover:-translate-y-0.5 hover:bg-lilac-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save name
          </button>
        </div>
        {nameStatus && <p role="status" data-testid="settings-name-status" className={`mt-3 ${statusClass(nameStatus)}`}>{nameStatus.text}</p>}
      </form>

      <div className="mt-8 border-t border-white/[0.07] pt-7">
        <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-paper/45">Password</h3>
        {hasPassword ? (
          <form onSubmit={changePassword} className="mt-4 space-y-4">
            <div>
              <label htmlFor="settings-current-password" className={labelClass}>Current password</label>
              <input
                id="settings-current-password"
                data-testid="settings-current-password"
                type="password"
                autoComplete="current-password"
                required
                value={pw.current}
                onChange={(e) => setPw((v) => ({ ...v, current: e.target.value }))}
                className={fieldClass}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="settings-new-password" className={labelClass}>New password</label>
                <input
                  id="settings-new-password"
                  data-testid="settings-new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={pw.next}
                  onChange={(e) => setPw((v) => ({ ...v, next: e.target.value }))}
                  placeholder="8+ characters"
                  className={fieldClass}
                />
              </div>
              <div>
                <label htmlFor="settings-confirm-password" className={labelClass}>Confirm new password</label>
                <input
                  id="settings-confirm-password"
                  data-testid="settings-confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={pw.confirm}
                  onChange={(e) => setPw((v) => ({ ...v, confirm: e.target.value }))}
                  className={fieldClass}
                />
              </div>
            </div>
            {pwStatus && <p role="status" data-testid="settings-password-status" className={statusClass(pwStatus)}>{pwStatus.text}</p>}
            <button
              type="submit"
              data-testid="settings-password-save"
              disabled={busy}
              className="rounded-full border border-lilac/40 bg-lilac/10 px-6 py-3 text-sm font-bold text-lilac transition-colors duration-200 hover:bg-lilac/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac disabled:cursor-not-allowed disabled:opacity-50"
            >
              Update password
            </button>
          </form>
        ) : (
          <p data-testid="settings-social-note" className="mt-4 text-sm text-paper/50">
            You signed up with {(user.providers || []).includes("apple") ? "Apple" : "Google"} — no password on file. Use the
            social button to sign in, or set a password via "Forgot password?" on the sign-in page.
          </p>
        )}
      </div>
    </div>
  );
};

const ORDER_STATUS = {
  reserved: { label: "Reserved — we'll be in touch", color: "#E8CE8F" },
  active: { label: "Active", color: "#B8A7E8" },
  complete: { label: "Complete", color: "#8FD8B8" },
};

const STEP_STATE = {
  pending: { label: "Not started", color: "rgba(246,243,252,0.35)" },
  in_progress: { label: "In progress", color: "#B8A7E8" },
  done: { label: "Done", color: "#8FD8B8" },
};

const PackageCard = ({ order }) => {
  const status = ORDER_STATUS[order.status] || ORDER_STATUS.reserved;
  const deliverables = order.deliverables || [];
  const total = deliverables.length || STEP_META.length;
  const doneCount = deliverables.length
    ? deliverables.filter((d) => d.state === "done").length
    : STEP_META.filter((s) => order.steps?.[s.key] === "done").length;
  return (
    <div data-testid={`package-card-${order.package}`} className="rounded-3xl border border-white/10 bg-card p-7 sm:p-9">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-extrabold text-paper">{order.package_name}</h2>
        <span
          data-testid="package-status"
          className="rounded-full px-3.5 py-1 text-xs font-bold uppercase tracking-[0.12em]"
          style={{ backgroundColor: `${status.color}1f`, color: status.color }}
        >
          {status.label}
        </span>
      </div>
      <div className="mt-5 flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/10">
          <div
            data-testid="package-progress-bar"
            className="h-full rounded-full bg-gradient-to-r from-lilac-deep to-lilac transition-[width] duration-500 ease-out"
            style={{ width: `${(doneCount / total) * 100}%` }}
          />
        </div>
        {deliverables.length > 0 && (
          <span data-testid="package-delivered-count" className="shrink-0 text-xs font-bold text-paper/45">
            {doneCount}/{total} delivered
          </span>
        )}
      </div>
      <div className="mt-6 space-y-3.5">
        {STEP_META.map((step) => {
          const state = STEP_STATE[order.steps?.[step.key]] || STEP_STATE.pending;
          const isDone = order.steps?.[step.key] === "done";
          const isActive = order.steps?.[step.key] === "in_progress";
          const stageItems = deliverables.filter((d) => d.stage === step.key);
          return (
            <div key={step.key} data-testid={`package-step-${step.key}`} className="rounded-2xl border border-white/[0.07] bg-ink/50 p-4">
              <div className="flex items-center gap-4">
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isActive ? "animate-pulse" : ""}`}
                  style={{
                    backgroundColor: isDone ? "rgba(143,216,184,0.15)" : isActive ? "rgba(184,167,232,0.15)" : "rgba(255,255,255,0.06)",
                    color: state.color,
                  }}
                >
                  {isDone ? (
                    <Check className="h-5 w-5" strokeWidth={2.6} aria-hidden="true" />
                  ) : (
                    <step.icon className="h-5 w-5" strokeWidth={2.2} aria-hidden="true" />
                  )}
                </span>
                <span className="min-w-0 flex-1 text-sm font-bold leading-snug text-paper/80">{step.title}</span>
                <span className="shrink-0 text-right text-[10px] font-bold uppercase tracking-[0.08em] sm:text-xs sm:tracking-[0.12em]" style={{ color: state.color }}>
                  {state.label}
                </span>
              </div>
              {stageItems.length > 0 && (
                <ul className="mt-3.5 space-y-2 border-t border-white/[0.05] pt-3.5" data-testid={`deliverables-${step.key}`}>
                  {stageItems.map((d) => {
                    const dState = STEP_STATE[d.state] || STEP_STATE.pending;
                    const itemFiles = (order.files || []).filter((f) => f.deliverable_key === d.key);
                    return (
                      <li key={d.key} data-testid={`deliverable-${d.key}`}>
                        <div className="flex items-center gap-2.5">
                          <span
                            aria-hidden="true"
                            className="h-1.5 w-1.5 shrink-0 rounded-full"
                            style={{ backgroundColor: d.state === "pending" ? "rgba(246,243,252,0.2)" : dState.color }}
                          />
                          <span className={`min-w-0 flex-1 text-xs ${d.state === "done" ? "text-paper/45 line-through decoration-white/20" : "text-paper/70"}`}>
                            {d.label}
                          </span>
                          <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.08em]" style={{ color: dState.color }}>
                            {dState.label}
                          </span>
                        </div>
                        {itemFiles.length > 0 && (
                          <div className="ml-4 mt-1.5 flex flex-wrap gap-1.5">
                            {itemFiles.map((f) => (
                              <a
                                key={f.id}
                                href={`${API}/api/files/${f.id}/download`}
                                data-testid={`file-download-${f.id}`}
                                className="inline-flex items-center gap-1.5 rounded-full border border-lilac/30 bg-lilac/10 px-3 py-1 text-[11px] font-bold text-lilac transition-colors duration-200 hover:bg-lilac/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3" aria-hidden="true">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" />
                                </svg>
                                {f.filename}
                              </a>
                            ))}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
      {order.status === "reserved" && (
        <p className="mt-6 text-sm text-paper/50">
          Your spot is held. We confirm scope, timeline, and payment together — then the steps above start moving.
        </p>
      )}
    </div>
  );
};

const Dashboard = ({ user }) => {
  const { signout } = useAuth();
  const [inquiries, setInquiries] = useState(null);
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/my/inquiries`, { credentials: "include" });
        setInquiries(res.ok ? await res.json() : []);
      } catch {
        setInquiries([]);
      }
      try {
        const res = await fetch(`${API}/api/my/orders`, { credentials: "include" });
        setOrders(res.ok ? await res.json() : []);
      } catch {
        setOrders([]);
      }
    })();
  }, []);

  return (
    <div data-testid="account-dashboard" className="mx-auto w-full max-w-3xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Eyebrow testId="account-eyebrow">Your account</Eyebrow>
          <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-paper sm:text-4xl">
            Hi, {user.name?.split(" ")[0] || "there"}.
          </h1>
          <p className="mt-2 text-sm text-paper/50">{user.email}</p>
        </div>
        <button
          type="button"
          data-testid="signout-button"
          onClick={signout}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-bold text-paper/70 transition-[border-color,color] duration-200 hover:border-lilac/50 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-ink"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          Sign out
        </button>
      </div>

      <div className="mt-10">
        {orders === null ? (
          <p className="text-sm text-paper/45">Loading…</p>
        ) : orders.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-card p-7 sm:p-9">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-xl font-extrabold text-paper">Your package</h2>
              <span data-testid="package-status" className="rounded-full border border-white/15 px-3.5 py-1 text-xs font-bold uppercase tracking-[0.12em] text-paper/50">
                No package yet
              </span>
            </div>
            <div className="mt-7 space-y-4">
              {STEP_META.map((step) => (
                <div key={step.key} data-testid={`package-step-${step.key}`} className="flex items-center gap-4 rounded-2xl border border-white/[0.07] bg-ink/50 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/[0.06] text-paper/40">
                    <step.icon className="h-5 w-5" strokeWidth={2.2} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1 text-sm font-bold leading-snug text-paper/80">{step.title}</span>
                  <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.08em] text-paper/35 sm:text-xs sm:tracking-[0.12em]">Not started</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-paper/50">
              The moment you start a package, live progress shows up here.{" "}
              <Link
                to="/business-in-a-box"
                data-testid="account-explore-package"
                className="font-bold text-lilac transition-colors duration-200 hover:text-lilac-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
              >
                Explore Business in a Box
              </Link>
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <PackageCard key={order.id} order={order} />
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 rounded-3xl border border-white/10 bg-card p-7 sm:p-9">
        <h2 className="font-display text-xl font-extrabold text-paper">Your inquiries</h2>
        {inquiries === null ? (
          <p className="mt-4 text-sm text-paper/45">Loading…</p>
        ) : inquiries.length === 0 ? (
          <p data-testid="inquiries-empty" className="mt-4 text-sm text-paper/50">
            No inquiries yet — use any "Build your brand" button to tell us what you'd like to build.
          </p>
        ) : (
          <ul data-testid="my-inquiries-list" className="mt-5 divide-y divide-white/[0.06]">
            {inquiries.map((inq) => (
              <li key={inq.id} className="py-4" data-testid={`my-inquiry-${inq.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-bold text-lilac">{inq.need}</span>
                  <span className="text-xs text-paper/40">
                    {new Date(inq.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                </div>
                {inq.idea && <p className="mt-1.5 text-sm text-paper/55">{inq.idea}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <SettingsCard user={user} />
    </div>
  );
};

export default function Account() {
  const { user } = useAuth();

  useEffect(() => {
    document.title = "Account — PepMuse";
    return () => {
      document.title = "PepMuse — Your peptide brand. Ready to launch.";
    };
  }, []);

  return (
    <section data-testid="account-page" className="pb-24 pt-[120px] sm:pt-[150px]">
      <div className="mx-auto w-full max-w-shell px-5 sm:px-8">
        {user === null ? (
          <p className="py-20 text-center text-sm text-paper/45">Loading…</p>
        ) : user === false ? (
          <Reveal>
            <AuthCard />
          </Reveal>
        ) : (
          <Reveal>
            <Dashboard user={user} />
          </Reveal>
        )}
      </div>
    </section>
  );
}
